import React, { useState, useEffect, useMemo } from "react";
import Hamburger from "hamburger-react";
import { ExpandLess, ExpandMore, Close } from "@mui/icons-material";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

const IS_PRODUCTION = process.env.REACT_APP_ENVIRONMENT === "production";

// The `playfabUsers` field names are legacy misnomers. The backend cron
// (RenderNow-backend authController.cronJob) refreshes `Yesterday*` to PlayFab's
// *current cumulative lifetime* counter on every run — so those are really
// all-time totals, not "yesterday". `ThisMonth*` is the genuine month-to-date
// delta (current cumulative − the baseline captured on the 1st). We relabel
// them truthfully here so admins aren't misled.
const nf = new Intl.NumberFormat();

const formatCount = (val) => {
  if (val === null || val === undefined || val === "") return "—";
  const n = Number(val);
  return Number.isFinite(n) ? nf.format(n) : "—";
};

// `total_time_spent` is tracked in minutes. Render it compactly as "72h 19m"
// instead of dumping a raw, unit-less minute count.
const formatMinutes = (val) => {
  if (val === null || val === undefined || val === "") return "—";
  const total = Number(val);
  if (!Number.isFinite(total)) return "—";
  if (total <= 0) return "0m";
  const hours = Math.floor(total / 60);
  const mins = Math.round(total % 60);
  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
};

// Ordered metrics for each row/footer — keeps formatting centralised and the
// header groups (All-time | This month) in lockstep with the cell order.
const STAT_COLUMNS = [
  { key: "YesterdayNewProjectCount", group: "all", label: "Projects", type: "count" },
  { key: "YesterdayRenderCount", group: "all", label: "Renders", type: "count" },
  { key: "YesterdayTotalTimeSpent", group: "all", label: "Time", type: "time" },
  { key: "ThisMonthNewProjectCount", group: "month", label: "Projects", type: "count" },
  { key: "ThisMonthRenderCount", group: "month", label: "Renders", type: "count" },
  { key: "ThisMonthTotalTimeSpent", group: "month", label: "Time", type: "time" },
];

const formatStat = (type, val) =>
  type === "time" ? formatMinutes(val) : formatCount(val);

// Fixed height (px) of the "All-time / This month" grouping row. The second
// header row (Projects/Renders/Time) is pinned this far below the top so both
// header rows stay stacked and sticky while the body scrolls. Both the row's
// `height` and the offset use this constant so they can't drift apart.
const HEADER_GROUP_H = 37;

const MenuPanel = ({
  menuOpen,
  setMenuOpen,
  adminSectionOpen,
  setAdminSectionOpen,
  playfabData,
  isModalOpen,
  setIsModalOpen,
  viewerMode = false,
  canShare = false,
  onShare,
  isLoggedIn = false,
  onLogout,
}) => {
  // Two-step confirm for Log Out (an accidental tap drops the current stream).
  const [confirmLogout, setConfirmLogout] = useState(false);
  // Reset the confirm state whenever the menu closes, so reopening never lands
  // straight on "Are you sure?".
  useEffect(() => {
    if (!menuOpen) setConfirmLogout(false);
  }, [menuOpen]);

  // Sort by display name (unnamed users last) and pre-compute the "Totals" row
  // once, so the render stays cheap and the footer always matches the body.
  const { sortedPlayers, totals } = useMemo(() => {
    const sorted = [...playfabData].sort((a, b) => {
      if (!a.DisplayName && !b.DisplayName) return 0;
      if (!a.DisplayName) return 1;
      if (!b.DisplayName) return -1;
      return a.DisplayName.localeCompare(b.DisplayName);
    });
    const sums = STAT_COLUMNS.reduce((acc, col) => {
      acc[col.key] = sorted.reduce((s, p) => s + (Number(p[col.key]) || 0), 0);
      return acc;
    }, {});
    return { sortedPlayers: sorted, totals: sums };
  }, [playfabData]);

  return (
    <>
      {/* Hamburger toggle */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          position: "fixed",
          top: 0,
          right: 0,
          zIndex: 10001,
        }}
      >
        <div className="render-menu-toggle-2">
          <Hamburger
            toggled={menuOpen}
            toggle={setMenuOpen}
            size={24}
            duration={0.4}
            color="white"
            label="Open menu"
          />
        </div>
      </div>

      {/* Slide-in panel */}
      {menuOpen && (
        <>
          <div className="render-menu-backdrop" onClick={() => setMenuOpen(false)} />
          <div className="render-menu-panel" role="dialog" aria-modal="true">
            <div className="render-menu-header">
              <div className="render-menu-title">Menu</div>
              <button
                className="render-menu-close"
                onClick={() => setMenuOpen(false)}
                aria-label="Close menu"
              >
                ×
              </button>
            </div>
            <div className="render-menu-content">
              <div className="render-menu-section">
                <div className="render-menu-section-title">Main</div>
                <button
                  className="render-menu-item"
                  onClick={() => {
                    // Ask App to open chat. On /render this lazy-loads Tawk on the
                    // first click (and opens it once ready); afterwards it just
                    // reveals + maximizes the already-loaded widget.
                    window.dispatchEvent(new Event("tawk:request-open"));
                    setMenuOpen(false);
                  }}
                >
                  Live Chat Support
                </button>
                <button
                  className="render-menu-item"
                  onClick={() => {
                    document.getElementById("tutorial-fab")?.click?.();
                    setMenuOpen(false);
                  }}
                >
                  Tutorials
                </button>
                <button
                  className="render-menu-item"
                  onClick={() => {
                    window.open(
                      "https://forms.monday.com/forms/ed24ff35b9056bc8ee13f920d6c5ee74?r=use1",
                      "_blank"
                    );
                    setMenuOpen(false);
                  }}
                >
                  Ideas & Feedback
                </button>
                {!IS_PRODUCTION && canShare && (
                  <button
                    className="render-menu-item"
                    onClick={() => {
                      onShare?.();
                      setMenuOpen(false);
                    }}
                  >
                    Share Viewer Link
                  </button>
                )}
              </div>

              {!viewerMode && (
              <div className="render-menu-section">
                <div className="render-menu-section-title">Navigation</div>
                <button
                  className="render-menu-item"
                  onClick={() => {
                    setMenuOpen(false);
                    window.open("/dashboard", "_blank");
                  }}
                >
                  Dashboard
                </button>
              </div>
              )}

              {!viewerMode && (
              <div className="render-menu-section">
                <div
                  className="render-menu-section-title"
                  onClick={() => setAdminSectionOpen(!adminSectionOpen)}
                  style={{
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  Admin
                  {adminSectionOpen ? <ExpandLess /> : <ExpandMore />}
                </div>
                {adminSectionOpen && (
                  playfabData.length ? (
                    <button
                      className="render-menu-item"
                      onClick={() => { setIsModalOpen(true); setMenuOpen(false); }}
                    >
                      User stats
                    </button>
                  ) : (
                    <button
                      className="render-menu-item"
                      onClick={() => {
                        // Open the admin login in a new tab so it never disrupts this
                        // stream/connection; the new tab auto-shows the login popup.
                        window.open("/render?action=adminlogin", "_blank");
                        setMenuOpen(false);
                      }}
                    >
                      Admin Login
                    </button>
                  )
                )}
              </div>
              )}

              {/* Log Out — resets the user and re-opens the login form over the
                  still-running stream (no reconnect), so a different account can
                  sign in. Only once the user has actually logged in, and never in
                  read-only viewer mode. Hidden in production (same as the Google/MS
                  sign-in buttons) until the UE side ships LogoutUser handling. */}
              {!IS_PRODUCTION && !viewerMode && isLoggedIn && (
              <div className="render-menu-section">
                {confirmLogout ? (
                  <div className="render-menu-confirm">
                    <div className="render-menu-confirm-text">
                      Log out of this account?
                    </div>
                    <div className="render-menu-confirm-actions">
                      <button
                        className="render-menu-item render-menu-confirm-cancel"
                        onClick={() => setConfirmLogout(false)}
                      >
                        Cancel
                      </button>
                      <button
                        className="render-menu-item render-menu-item--logout"
                        onClick={() => {
                          setConfirmLogout(false);
                          setMenuOpen(false);
                          onLogout?.();
                        }}
                      >
                        Log Out
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    className="render-menu-item render-menu-item--logout"
                    onClick={() => setConfirmLogout(true)}
                  >
                    Log Out
                  </button>
                )}
              </div>
              )}
            </div>
            <div className="render-menu-footer">
              <div className="render-menu-note">
                When inactive for 20 minutes, the connection will automatically disconnect.
              </div>
            </div>
          </div>
        </>
      )}

      {/* Admin user stats dialog */}
      {playfabData.length > 0 && (
        <Dialog
          open={isModalOpen}
          fullWidth
          disableScrollLock
          onClose={() => setIsModalOpen(false)}
          maxWidth="lg"
          sx={{ zIndex: 10006 }}
          PaperProps={{ sx: { borderRadius: 2, overflow: "hidden" } }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              pb: 1,
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                User stats
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {sortedPlayers.length}{" "}
                {sortedPlayers.length === 1 ? "user" : "users"} · all-time totals
                and month-to-date activity
              </Typography>
            </Box>
            <IconButton
              aria-label="Close"
              onClick={() => setIsModalOpen(false)}
              size="small"
            >
              <Close fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 1 }}>
            <TableContainer
              component={Paper}
              variant="outlined"
              // Cap the height so the middle rows scroll *inside* this box —
              // that's what lets the header rows (sticky top) and the Totals
              // row (sticky bottom) stay pinned. Without a bounded height the
              // whole dialog scrolls and stickiness has nothing to stick to.
              sx={{ borderRadius: 2, maxHeight: "62vh", overflow: "auto" }}
            >
              <Table size="small" stickyHeader sx={{ minWidth: 720 }}>
                <TableHead>
                  {/* Grouping row: makes the All-time vs This-month split explicit
                      so the bare numbers can't be misread as a single timeframe. */}
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        height: HEADER_GROUP_H,
                        py: 0,
                        backgroundColor: "#fafafa",
                        position: "sticky",
                        top: 0,
                        left: 0,
                        // Corner cell (top + left sticky) must sit above the
                        // top-only and left-only sticky cells.
                        zIndex: 4,
                      }}
                    >
                      User
                    </TableCell>
                    <TableCell
                      align="center"
                      colSpan={3}
                      sx={{
                        fontWeight: 700,
                        height: HEADER_GROUP_H,
                        py: 0,
                        color: "text.secondary",
                        letterSpacing: 0.4,
                        textTransform: "uppercase",
                        fontSize: 11,
                        borderLeft: "1px solid #eee",
                        backgroundColor: "#fafafa",
                        position: "sticky",
                        top: 0,
                        zIndex: 3,
                      }}
                    >
                      All-time
                    </TableCell>
                    <TableCell
                      align="center"
                      colSpan={3}
                      sx={{
                        fontWeight: 700,
                        height: HEADER_GROUP_H,
                        py: 0,
                        color: "primary.main",
                        letterSpacing: 0.4,
                        textTransform: "uppercase",
                        fontSize: 11,
                        borderLeft: "1px solid #eee",
                        backgroundColor: "#f3f7ff",
                        position: "sticky",
                        top: 0,
                        zIndex: 3,
                      }}
                    >
                      This month
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        backgroundColor: "#fafafa",
                        position: "sticky",
                        top: HEADER_GROUP_H,
                        left: 0,
                        zIndex: 4,
                      }}
                    />
                    {STAT_COLUMNS.map((col, i) => (
                      <TableCell
                        key={`${col.group}-${col.label}`}
                        align="right"
                        sx={{
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                          backgroundColor:
                            col.group === "month" ? "#f3f7ff" : "#fafafa",
                          borderLeft: i === 3 ? "1px solid #eee" : undefined,
                          position: "sticky",
                          // Pinned directly beneath the grouping row.
                          top: HEADER_GROUP_H,
                          zIndex: 3,
                        }}
                      >
                        {col.label}
                        {col.type === "time" && (
                          <Typography
                            component="span"
                            variant="caption"
                            color="text.secondary"
                            sx={{ ml: 0.5 }}
                          >
                            (hrs)
                          </Typography>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedPlayers.map((data) => (
                    <TableRow
                      key={data.PlayerId}
                      hover
                      sx={{ "&:nth-of-type(odd)": { backgroundColor: "#fcfcfd" } }}
                    >
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                          position: "sticky",
                          left: 0,
                          zIndex: 1,
                          backgroundColor: "inherit",
                        }}
                      >
                        {data.DisplayName || "—"}
                      </TableCell>
                      {STAT_COLUMNS.map((col, i) => (
                        <TableCell
                          key={col.key}
                          align="right"
                          sx={{
                            fontVariantNumeric: "tabular-nums",
                            whiteSpace: "nowrap",
                            color:
                              col.group === "month"
                                ? "text.primary"
                                : "text.secondary",
                            borderLeft: i === 3 ? "1px solid #eee" : undefined,
                          }}
                        >
                          {formatStat(col.type, data[col.key])}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
                {sortedPlayers.length > 1 && (
                  <TableFooter>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          fontSize: 13,
                          color: "text.primary",
                          position: "sticky",
                          // Pinned to the bottom of the scroll box; corner cell
                          // (bottom + left) sits above the edge-sticky cells.
                          bottom: 0,
                          left: 0,
                          zIndex: 4,
                          backgroundColor: "#fafafa",
                          borderTop: "2px solid #e0e0e0",
                        }}
                      >
                        Totals
                      </TableCell>
                      {STAT_COLUMNS.map((col, i) => (
                        <TableCell
                          key={col.key}
                          align="right"
                          sx={{
                            fontWeight: 700,
                            fontSize: 13,
                            color: "text.primary",
                            fontVariantNumeric: "tabular-nums",
                            whiteSpace: "nowrap",
                            backgroundColor: "#fafafa",
                            borderTop: "2px solid #e0e0e0",
                            borderLeft: i === 3 ? "1px solid #eee" : undefined,
                            position: "sticky",
                            bottom: 0,
                            zIndex: 3,
                          }}
                        >
                          {formatStat(col.type, totals[col.key])}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableFooter>
                )}
              </Table>
            </TableContainer>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 1.5 }}
            >
              All-time figures are cumulative since each account was created.
              This month resets on the 1st. Time is total session time.
            </Typography>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default MenuPanel;
